// Libraries
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { chunk, get, intersection, remove } from 'lodash';
import { noop } from 'src/utils/event';
import cn from 'classnames';
// Constants
import { QA_FLOW_ANSWER_LAYOUTS } from 'src/containers/AddSkuPage/QuestionsFlowPage/constants';
// Components
import { Grid } from 'src/components/HTKit/Grid';
import { ProgressButton } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts';
import { CheckboxPanel } from 'src/components/HTKit/Forms/CheckboxV2QA/Parts';
import { DEFAULT_QUESTIONS_PER_COLUMN } from '../constants';
// Styles
import styles from '../styles.scss';

const getLegacyColumnGroups = (children) => {
  if (children.length === 5) return chunk(children, 3);
  return chunk(children, DEFAULT_QUESTIONS_PER_COLUMN);
};

const CheckboxContainer = ({ children = [], layout }) => {
  const getRowLayout = () =>
    // Return an array of React elements
    children.map((answer, answerIndex) => (
      <Grid.Column
        key={`answer${answerIndex}`}
        sm={4}
        md={4}
        lg={children.length < 2 ? 12 : 6}
        classes={styles.overrideGridColumn}
      >
        {answer}
      </Grid.Column>
    ));
  const getColumnLayout = () => {
    // Return an array of React elements
    const legacyColumnGroups = getLegacyColumnGroups(children);
    return legacyColumnGroups.map((columnGroup, index) => (
      <Grid.Column
        key={`columnGroup${index}`}
        sm={4}
        md={legacyColumnGroups.length < 2 ? 8 : 4}
        lg={legacyColumnGroups.length < 2 ? 12 : 6}
      >
        {columnGroup}
      </Grid.Column>
    ));
  };

  return (
    <article className="site-v2">
      <Grid.Fluid classes={styles.checkboxContainerGrid}>
        <Grid.Row classes={cn(styles.checkboxRowGrid)}>
          {layout === QA_FLOW_ANSWER_LAYOUTS.ROW ? getRowLayout() : getColumnLayout()}
        </Grid.Row>
      </Grid.Fluid>
    </article>
  );
};

const CheckboxGroupV2QA = (props) => {
  const { answers, onChange, complete, required, onClickUpdate = noop, layout } = props;
  const [checkboxValues, setCheckboxValues] = useState([]);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [childrenIds, setChildrenIds] = useState([]);
  const buttonCTAText = complete ? 'Add to Cart' : 'Continue';

  /**
   * This is for real time updating of price panel. Allows for a updater callback
   * @param value {add: string|number, remove: string|number}
   * @returns {*}
   */
  const onClickUpdater = (value) => {
    onClickUpdate(value);
  };

  const onCheckboxChange = ({ event, children }) => {
    const checkboxId = event.target.value;
    const isChecked = event.target.checked;
    const childIds = children && children.map((c) => c.id);

    if (isChecked) {
      setCheckboxValues([...checkboxValues, checkboxId]);
      onClickUpdater({ add: event.target.value, remove: '' });
      if (childIds) setChildrenIds([...childrenIds, ...childIds]);
    }

    if (!isChecked) {
      const newCheckboxList = [...checkboxValues];
      const childIdToRemove = intersection(checkboxValues, childIds)[0];
      [checkboxId, childIdToRemove].forEach((val, idx) => {
        if (idx === 1 && val === undefined) return;
        const itemIndex = newCheckboxList.indexOf(val);
        newCheckboxList.splice(itemIndex, 1);
        setCheckboxValues(newCheckboxList);
      });

      onClickUpdater({ add: '', remove: event.target.value || childIdToRemove });
      if (childIds) {
        const newChildList = [...childrenIds];
        remove(newChildList, (elem) => childIds.includes(elem));
        setChildrenIds(newChildList);
      }
    }
  };

  const onAddToCartClick = () => {
    onChange(checkboxValues);
  };

  const handleOnChange = (answer) => (event) => {
    onCheckboxChange({ event, children: answer.children });
  };

  const handleDropdownChange = (values) => {
    const newList = [...checkboxValues, values.add];
    if (values.remove) remove(newList, (v) => v === values.remove);
    setCheckboxValues(newList);
    setButtonDisabled(false);
    onClickUpdater(values);
  };

  const isButtonDisabled = () => {
    const disabledDefaultCheck = required && !checkboxValues.length;
    if (disabledDefaultCheck) return true;
    if (buttonDisabled) return true;
    return false;
  };

  const handleButtonDisabling = () => {
    const checkboxWithDropdownSelected = checkboxValues.includes('');
    const numDropdownsSelected = checkboxValues.filter((val) => val === '').length;
    const allDropdownsSelected =
      intersection(checkboxValues, childrenIds).length === numDropdownsSelected;
    const shouldDisable = checkboxWithDropdownSelected && !allDropdownsSelected;
    setButtonDisabled(shouldDisable);
  };

  useEffect(() => {
    handleButtonDisabling();
  }, [checkboxValues, childrenIds]);

  return (
    <>
      <CheckboxContainer layout={layout}>
        {answers &&
          answers.map((answer, i) => {
            let { label } = answer;
            if (answer.adjAmountFormatted) label += ` +${answer.adjAmountFormatted}`;
            const options = get(answer, 'children', []);
            const showDropdown = Boolean(get(options, 'length', 0));
            const childIds = answer.children && answer.children.map((c) => c.id);
            const isChildSelected = intersection(checkboxValues, childIds)[0];
            const showErrorStyles =
              buttonDisabled && checkboxValues.length >= 2 && !isChildSelected
                ? styles.dropdownColor
                : null;
            const panelStyles = cn(styles.checkboxPanel, 'marginBottom-small', {
              [styles.column]: layout === QA_FLOW_ANSWER_LAYOUTS.COLUMN,
              [styles.row]: layout === QA_FLOW_ANSWER_LAYOUTS.ROW,
            });
            return (
              <CheckboxPanel
                {...props}
                options={options}
                onChange={handleOnChange(answer)}
                key={i}
                showDropdown={showDropdown}
                checkboxValue={answer.id}
                checkboxLabel={label}
                hintText={answer.hint}
                onDropdownChange={handleDropdownChange}
                dropdownClassName={showErrorStyles}
                className={panelStyles}
                answer={answer}
              />
            );
          })}
      </CheckboxContainer>
      <ProgressButton
        buttonText={buttonCTAText}
        onClick={onAddToCartClick}
        isDisabled={isButtonDisabled()}
      />
    </>
  );
};

CheckboxGroupV2QA.propTypes = {
  answers: PropTypes.array,
  onChange: PropTypes.any,
  onClickUpdate: PropTypes.any,
  complete: PropTypes.bool,
  required: PropTypes.bool,
  layout: PropTypes.oneOf([QA_FLOW_ANSWER_LAYOUTS.COLUMN, QA_FLOW_ANSWER_LAYOUTS.ROW]),
};
CheckboxGroupV2QA.defaultProps = { layout: QA_FLOW_ANSWER_LAYOUTS.COLUMN };

CheckboxContainer.propTypes = {
  children: PropTypes.node,
  layout: PropTypes.oneOf([QA_FLOW_ANSWER_LAYOUTS.COLUMN, QA_FLOW_ANSWER_LAYOUTS.ROW]),
};

export default React.memo(CheckboxGroupV2QA);

/*
Usage example:
import { CheckboxGroupV2QA } from 'src/components/HTKit/Forms/CheckboxV2QA';
  <CheckboxGroupV2QA
    onChange={udpateQAFlow}
    answers={answers}
    complete={boolean}
    required={boolean}
  />
*/
